/**
 * QuestScreen — Phase 2 patch 4: Daily quests.
 *
 * Hiển thị 4 quest hôm nay với progress bar + nút Claim khi completed.
 * Backend endpoints:
 *   - GET /v1/quests/today
 *   - POST /v1/quests/{code}/claim
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { api, extractErrorMessage } from "../lib/api";
import { newActionId } from "../lib/idempotency";
import { BottomNav } from "../scenes/MainCafe/components/BottomNav";
import { useCafe3dStore } from "../stores/cafe3dStore";

interface QuestItem {
  code: string;
  name_key: string;
  description_key: string | null;
  reward_bean: number;
  progress: number;
  target_value: number;
  claimed: boolean;
  is_completed: boolean;
}

const QUEST_ICON: Record<string, string> = {
  serve_10_correct: "☕",
  earn_200_bean: "💰",
  serve_3_drinks: "🍵",
  try_phase2_drink: "🌟",
};

export function QuestScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useTelegramBackButton("/");
  const beanCoin = useCafe3dStore((s) => s.beanCoin);
  const initFromBackend = useCafe3dStore((s) => s.initFromBackend);

  const [quests, setQuests] = useState<QuestItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get<{ data: { quests: QuestItem[] } }>(
          "/quests/today",
        );
        if (!cancelled) setQuests(res.data.data.quests);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function claim(code: string) {
    setBusy(code);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{
        data: { quest_code: string; reward_bean: number };
      }>(`/quests/${code}/claim`, { client_action_id: newActionId() });
      setSuccess(
        t("quest.claimed_success", { reward: res.data.data.reward_bean }),
      );
      // Reload quest list + shop state.
      const list = await api.get<{ data: { quests: QuestItem[] } }>(
        "/quests/today",
      );
      setQuests(list.data.data.quests);
      await initFromBackend();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="w-full h-full max-w-[480px] mx-auto flex flex-col bg-cream-50 relative overflow-hidden safe-area-top">
      {/* Header */}
      <div className="px-4 py-3 bg-coffee-900 text-cream-50 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-sm opacity-80 hover:opacity-100"
        >
          {t("gameplay.back_to_shop")}
        </button>
        <h1 className="text-base font-bold font-baloo">
          {t("quest.screen_title")}
        </h1>
        <div className="text-sm font-baloo">
          ☕ {beanCoin.toLocaleString()}
        </div>
      </div>

      {/* Subtitle */}
      <div className="px-4 py-2 bg-cream-100 text-xs text-coffee-700">
        {t("quest.subtitle")}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {error && (
          <div className="bg-red-stool/10 border border-red-stool/30 text-red-stool rounded-lg px-3 py-2 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-leaf/10 border border-green-leaf/30 text-green-leaf rounded-lg px-3 py-2 text-sm">
            {success}
          </div>
        )}

        {quests === null ? (
          <div className="text-center text-coffee-700 py-8">
            {t("quest.loading")}
          </div>
        ) : quests.length === 0 ? (
          <div className="text-center text-coffee-700 py-8">
            {t("quest.empty")}
          </div>
        ) : (
          quests.map((q) => {
            const pct = Math.min(
              100,
              Math.round((q.progress / q.target_value) * 100),
            );
            const isBusy = busy === q.code;

            return (
              <div
                key={q.code}
                className={`bg-white rounded-2xl p-4 shadow-soft-amber border-2 transition ${
                  q.claimed
                    ? "border-coffee-700/10 opacity-60"
                    : q.is_completed
                      ? "border-green-leaf/40"
                      : "border-coffee-700/10"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-cream flex items-center justify-center text-2xl">
                    {QUEST_ICON[q.code] ?? "📋"}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-coffee-900 text-sm">
                      {t(`quest.${q.code}.name`)}
                    </h3>
                    <p className="text-xs text-coffee-700 mt-0.5">
                      {t(`quest.${q.code}.description`)}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-coffee-700/60">
                      {t("hud.bean")}
                    </div>
                    <div className="text-sm font-bold text-coffee-900">
                      +{q.reward_bean}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-coffee-700 mb-1">
                    <span>
                      {t("quest.progress")}: {q.progress}/{q.target_value}
                    </span>
                    <span className="font-bold">{pct}%</span>
                  </div>
                  <div className="h-2 bg-coffee-700/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        q.claimed
                          ? "bg-coffee-700/40"
                          : q.is_completed
                            ? "bg-green-leaf"
                            : "bg-yellow-light"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                {/* Claim button */}
                <button
                  type="button"
                  onClick={() => claim(q.code)}
                  disabled={!q.is_completed || q.claimed || isBusy}
                  className={`mt-3 w-full py-2 rounded-xl font-bold text-sm transition ${
                    q.claimed
                      ? "bg-coffee-700/10 text-coffee-700/50 cursor-not-allowed"
                      : q.is_completed
                        ? "bg-green-leaf text-cream-50 shadow-[0_3px_0_#3F6536] active:translate-y-[1px] active:shadow-[0_2px_0_#3F6536]"
                        : "bg-coffee-700/15 text-coffee-700/50 cursor-not-allowed"
                  }`}
                >
                  {q.claimed
                    ? t("quest.claimed")
                    : isBusy
                      ? t("quest.claiming")
                      : q.is_completed
                        ? t("quest.claim_button")
                        : t("quest.in_progress")}
                </button>
              </div>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
}
